import { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useDispatch } from "react-redux";
import { Form, Input, Space, Tag, Tooltip, Typography } from "antd";
import { useSearchParams } from "react-router-dom";

import { ListPageContainer } from "@/components";
import { loadingActions, superAppActions } from "@/redux/actions";
import {
    DATETIME_FORMAT_DISPLAY,
    DATETIME_FORMAT_VALUE,
    commonStatus,
    fieldTypes,
} from "@/constants";
import { formatDate } from "@/utils/date";
import { appPlatformDDL, statusDDL } from "@/constants/masterData";
import { useNotification } from "@/hooks";
import paths from "@/constants/paths";

import { FileExcelOutlined, PictureOutlined } from "@ant-design/icons";
import { useEffect } from "react";

dayjs.extend(utc);

const InputComp = ({ data, onChangeValue, ...inputProps }) => {
    const handleChange = (e) => {
        inputProps.onChange?.(inputProps.value ? e.target.value : data);
        onChangeValue?.(e.target.value);
    };
    useEffect(() => {
        if (!data) return;

        inputProps.onChange?.(inputProps.value ? inputProps.value : data);
    }, [data]);

    return <Input {...inputProps} onChange={handleChange} />;
};

const InputField = ({ fieldName, value, onChange }) => {
    return (
        <Form.Item name={fieldName}>
            <InputComp data={value} onChangeValue={onChange} />
        </Form.Item>
    );
};

const ListPage = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const { showErrorMessage, showSuccessMessage } = useNotification();

    const [fromDate, setFromDate] = useState(searchParams.get("fromDate"));
    const [toDate, setToDate] = useState(searchParams.get("toDate"));
    const [priorities, setPriorities] = useState({});
    const [rollBackPriorities, setRollBackPriorities] = useState({});

    const searchFields = [
        {
            key: "appPlatform",
            searchPlaceholder: "App Platform",
            fieldType: fieldTypes.SELECT,
            options: appPlatformDDL,
            gridCol: 8,
        },
        {
            key: "appName",
            searchPlaceholder: "Tên mini app",
            gridCol: 8,
        },
        {
            key: "status",
            searchPlaceholder: "Trạng thái",
            fieldType: fieldTypes.SELECT,
            options: statusDDL,
            gridCol: 8,
        },
        {
            key: "fromDate",
            searchPlaceholder: "Ngày tạo từ",
            fieldType: fieldTypes.DATE,
            gridCol: 8,
            disabledDate: (current) => {
                //fromDate chỉ tối đa 3 tháng gần nhất (trong quá khứ) so với ngày hiện tại
                //phải nhỏ hơn toDate
                //nhỏ hơn ngày hiện tại
                return (
                    current &&
                    (current < dayjs().add(-3, "months").startOf("day") ||
                        current > dayjs(toDate).endOf?.("day") ||
                        current > dayjs().endOf("day"))
                );
            },
            onChange: (fromDate) => setFromDate(fromDate),
        },
        {
            key: "toDate",
            searchPlaceholder: "Đến ngày",
            fieldType: fieldTypes.DATE,
            gridCol: 8,
            disabledDate: (current) => {
                const currfromDate = fromDate ? dayjs(fromDate) : null;
                //toDate chỉ tối đa 3 tháng gần nhất (trong quá khứ) so với ngày hiện tại
                //phải lớn hơn fromDate
                //nhỏ hơn ngày hiện tại
                return (
                    current &&
                    (current < dayjs().add(-3, "months").startOf("day") ||
                        current < currfromDate?.startOf?.("day") ||
                        current > dayjs().endOf("day"))
                );
            },
            onChange: (toDate) => setToDate(toDate),
        },
    ];

    const mappingSearchParams = (searchFields) => {
        const filter = {};
        searchFields?.forEach((searchField) => {
            if (searchParams.has(searchField.key)) {
                if (searchField.key === "fromDate") {
                    filter[searchField.key] = dayjs
                        .utc(searchParams.get(searchField.key))
                        .format(DATETIME_FORMAT_VALUE);
                } else if (searchField.key === "toDate") {
                    filter[searchField.key] = dayjs
                        .utc(searchParams.get(searchField.key))
                        .endOf("day")
                        .format(DATETIME_FORMAT_VALUE);
                } else {
                    filter[searchField.key] = searchParams.get(searchField.key);
                }
            }
        });

        return filter;
    };

    const onExportExcel = (e, getList, list) => {
        if (!list || list?.length < 1) {
            e.preventDefault();
            showErrorMessage("Cần ít nhất 1 row để export!");
            return;
        }

        dispatch(loadingActions.showLoadingFullScreen());

        dispatch(
            superAppActions.export({
                params: mappingSearchParams(searchFields),
                onCompleted: (data) => {
                    dispatch(loadingActions.hideLoadingFullScreen());
                    const a = document.createElement("a");
                    a.href = window.URL.createObjectURL(data);
                    a.download = `SuperAppList-${dayjs().format(
                        "DD-MM-YYYY"
                    )}.xlsx`;
                    a.click();
                },
                onError: (err) => {
                    dispatch(loadingActions.hideLoadingFullScreen());
                    showErrorMessage(
                        "Export Super Apps thất bại. Vui lòng thử lại!"
                    );
                },
            })
        );
    };

    const onResetSearch = () => {
        setFromDate(null);
        setToDate(null);
    };

    const onUpdatePriority = (record, priority) => {
        console.log({ priority });
        if (priority == (rollBackPriorities[record.id] || record.priority))
            return;

        dispatch(
            superAppActions.updatePriority({
                params: {
                    id: record.id,
                    priority,
                },
                onCompleted: () => {
                    setPriorities({ ...priorities, [record.id]: priority });
                    setRollBackPriorities({
                        ...rollBackPriorities,
                        [record.id]: priority,
                    });
                    showSuccessMessage("Cập nhật thứ tự ưu tiên thành công!");
                },
                onError: () => {
                    setPriorities({
                        ...priorities,
                        [record.id]:
                            rollBackPriorities[record.id] || record.priority,
                    });
                    showErrorMessage(
                        "Cập nhật thứ tự ưu tiên thất bại. Vui lòng thử lại!"
                    );
                },
            })
        );
    };

    return (
        <ListPageContainer
            objectName="Super App"
            createUrl={paths.superAppCreate}
            detailUrl={paths.superAppDetail}
            columns={[
                {
                    title: "Icon",
                    dataIndex: "imageUrl",
                    render: (imageUrl) =>
                        imageUrl ? (
                            <img
                                src={imageUrl}
                                style={{ heigth: 48, width: 48 }}
                                alt={"logo"}
                            />
                        ) : (
                            <PictureOutlined className="empty-img-col" />
                        ),
                },
                { title: "App Platform", dataIndex: "appPlatform" },
                {
                    title: "Tên mini app",
                    dataIndex: "appName",
                    render: (appName, rowData) => {
                        return (
                            <Tooltip
                                title={
                                    rowData.md5Ios || rowData.md5Android ? (
                                        <>
                                            {rowData.md5Ios ? (
                                                <>
                                                    IOS:{" "}
                                                    <Typography.Text
                                                        style={{
                                                            color: "white",
                                                        }}
                                                        copyable
                                                    >
                                                        {rowData.md5Ios}
                                                    </Typography.Text>
                                                    <br />
                                                </>
                                            ) : null}
                                            {rowData.md5Android ? (
                                                <>
                                                    Android:{" "}
                                                    <Typography.Text
                                                        style={{
                                                            color: "white",
                                                        }}
                                                        copyable
                                                    >
                                                        {rowData.md5Android}
                                                    </Typography.Text>
                                                </>
                                            ) : null}
                                        </>
                                    ) : null
                                }
                                overlayInnerStyle={{
                                    minWidth: 400,
                                }}
                            >
                                <div
                                    style={
                                        rowData.md5Ios || rowData.md5Android
                                            ? {
                                                  color: "#0052cc",
                                                  cursor: "pointer",
                                              }
                                            : {}
                                    }
                                >
                                    {appName}
                                </div>
                            </Tooltip>
                        );
                    },
                },
                {
                    title: "Phiên bản",
                    dataIndex: "appVersion",
                },
                {
                    title: "Phiên bản IOS",
                    dataIndex: "appVersionIos",
                },
                {
                    title: "Phiên bản Android",
                    dataIndex: "appVersionAndroid",
                },
                {
                    title: "Dung lượng IOS",
                    dataIndex: "sizePackageIos",
                },
                {
                    title: "Dung lượng Android",
                    dataIndex: "sizePackageAndroid",
                },
                {
                    title: "Thứ tự ưu tiên",
                    dataIndex: "priority",
                    render: (priority, record) => {
                        return (
                            <Form
                                onFinish={(values) =>
                                    onUpdatePriority(record, values.priority)
                                }
                                initialValues={{ priority }}
                                onBlur={(e) =>
                                    onUpdatePriority(record, e.target.value)
                                }
                            >
                                <InputField
                                    fieldName={"priority"}
                                    value={priorities[record.id]}
                                    onChange={() =>
                                        setPriorities({
                                            ...priorities,
                                            [record.id]: undefined,
                                        })
                                    }
                                />
                            </Form>
                        );
                    },
                },
                {
                    title: "Cập nhật cuối",
                    dataIndex: "updatedDate",
                    render: (updatedDate) => {
                        const dateTime = formatDate(
                            updatedDate,
                            DATETIME_FORMAT_DISPLAY
                        );
                        const dateTimeSplit = dateTime
                            ? dateTime.split(" ")
                            : [];
                        return dateTimeSplit.length ? (
                            <Space
                                direction="vertical"
                                size={1}
                                style={{ minWidth: 80 }}
                            >
                                <Typography.Text>
                                    {dateTimeSplit[1]}
                                </Typography.Text>
                                <Typography.Text>
                                    {dateTimeSplit[0]}
                                </Typography.Text>
                            </Space>
                        ) : (
                            <Typography.Text>N/A</Typography.Text>
                        );
                    },
                },
                {
                    title: "Trạng thái",
                    dataIndex: "status",
                    render: (status) => (
                        <Tag
                            style={{ minWidth: 70, textAlign: "center" }}
                            color={
                                [commonStatus.ACTIVE].includes(status)
                                    ? "success"
                                    : "error"
                            }
                        >
                            {status.replace("_", " ")}
                        </Tag>
                    ),
                },
            ]}
            searchFields={searchFields}
            buttonActions={[
                {
                    label: "Export",
                    icon: <FileExcelOutlined />,
                    ghost: true,
                    onClick: onExportExcel,
                },
            ]}
            actionBar={{
                isEdit: true,
                isUpdateStatus: true,
            }}
            mappingSearchParams={mappingSearchParams}
            updateStatusAction={superAppActions.updateStatus}
            getListAction={superAppActions.getList}
            onResetSearchParams={onResetSearch}
        />
    );
};

export default ListPage;
